import { DOCUMENT } from '@angular/common';
import { inject, InjectionToken } from '@angular/core';

import { environment } from '../../../environments/environment';

/**
 * Who the login page says it is, by the host it is served on.
 *
 * <p>The SPA is one build served on every bound hostname. On a PRODUCT's own front door
 * (`app.batchbilling.net`) a biller signs in to that product, not to the platform behind it - card 198
 * took the platform's name off everything Batch Billing shows, and this page was the last of it.
 * AuthServer keeps that login on the product host (Application:ProductAuthHosts); this decides what
 * it looks like there. Anywhere else the page is unbranded, as it always was.</p>
 */
export interface ProductBrand {
  /** The product's name, as the heading and the browser tab say it. */
  readonly name: string;
  /** Under the SPA's base href, so it resolves on every host the build is served from. */
  readonly logo: string;
  /** The tab icon, under the base href like {@link logo}. */
  readonly icon: string;
  /**
   * Whether "Register" is offered. Batch Billing's people are added by their administrator; a
   * self-registered account there belongs to no company and can do nothing.
   */
  readonly selfRegistration: boolean;
}

const BRANDS: ReadonlyArray<{ readonly domain: string; readonly brand: ProductBrand }> = [
  {
    domain: 'batchbilling.net',
    brand: {
      name: 'Batch Billing',
      logo: 'brands/batchbilling-logo.png',
      icon: 'brands/batchbilling-favicon.ico',
      selfRegistration: false,
    },
  },
];

/**
 * The hostname the page is served on. A token rather than `window.location` so a test can render the
 * login as a product host would see it.
 */
export const SERVED_HOSTNAME = new InjectionToken<string>('SERVED_HOSTNAME', {
  providedIn: 'root',
  factory: () => inject(DOCUMENT).location.hostname,
});

/** The brand for `hostname` - the domain itself or any host under it - or null for the platform. */
export function productBrandFor(hostname: string): ProductBrand | null {
  return onDomain(BRANDS, hostname)?.brand ?? null;
}

/**
 * What the browser tab shows on a host. The platform's own icon and name ("ComputAll") are the
 * defaults in index.html; a host replaces them only when it has a brand.
 */
export interface TabBrand {
  /** The tab's title for as long as no page sets a more specific one. */
  readonly title: string;
  /** The tab icon's URL; null keeps the platform's icon but still takes the title. */
  readonly icon: string | null;
  /** The 180px home-screen icon iOS uses; null leaves the platform's. */
  readonly touchIcon: string | null;
}

/** What `GET public/branding` answers on a client's custom domain (204, so null, anywhere else). */
export interface PublicBranding {
  readonly title: string;
  /** Relative to the API base, e.g. `public/branding/favicon.ico?v=42`. */
  readonly iconPath: string | null;
  readonly touchIconPath: string | null;
  /** The client's website logo, which the login page shows; null when none is uploaded. */
  readonly logoPath: string | null;
}

/**
 * This host's branding from the API, asked ONCE per page load and shared by the tab (applyTabBrand)
 * and the login page. Resolves null on a product host (never asked: its brand is in code), on the
 * platform's own hosts (204), and when the API cannot be reached.
 */
export const HOST_BRANDING = new InjectionToken<Promise<PublicBranding | null>>('HOST_BRANDING', {
  providedIn: 'root',
  factory: () =>
    productBrandFor(inject(SERVED_HOSTNAME))
      ? Promise.resolve(null)
      : fetchPublicBranding(environment.apiBaseUrl).catch(() => null),
});

/** What the login page shows: whose sign-in this is, its logo, and whether Register is offered. */
export interface LoginBrand {
  readonly name: string;
  /** A URL the page can load as-is; null shows the name alone. */
  readonly logo: string | null;
  readonly selfRegistration: boolean;
}

/** The platform's own login (app.computall.us, dev and test, localhost): ComputAll's. */
export const PLATFORM_LOGIN_BRAND: LoginBrand = {
  name: 'ComputAll',
  logo: 'brands/computall-logo.png',
  selfRegistration: true,
};

/**
 * The login brand for a host, in the same order as the tab: a PRODUCT host from code, a client's
 * custom domain from what that client uploaded, and everything else the platform's. Self-registration
 * stays on except where a product turns it off.
 */
export function loginBrandFor(
  product: ProductBrand | null,
  branding: PublicBranding | null,
  apiBaseUrl: string,
): LoginBrand {
  if (product) {
    return { name: product.name, logo: product.logo, selfRegistration: product.selfRegistration };
  }
  if (branding) {
    const api = apiBaseUrl.replace(/\/$/, '');
    return { name: branding.title, logo: branding.logoPath ? `${api}/${branding.logoPath}` : null, selfRegistration: true };
  }
  return PLATFORM_LOGIN_BRAND;
}

/**
 * Brands the tab for `hostname`, once, at boot.
 *
 * <p>Two sources, in order. A PRODUCT host (batchbilling.net) is branded from {@link productBrandFor},
 * in code, at once: it is not any one client's domain and must never be resolved as one. Any other
 * host asks the API, which answers for a client's custom domain from what that client uploaded
 * (self-serve-brand-icons plan) and with nothing for the platform's own hosts.</p>
 *
 * <p>Never throws and never blocks boot: an API that is slow or down leaves the ComputAll defaults,
 * which is what an unbranded host shows anyway.</p>
 */
export async function applyTabBrand(
  document: Document,
  hostname: string,
  fetchBranding: () => Promise<PublicBranding | null>,
  apiBaseUrl: string,
): Promise<void> {
  const product = productBrandFor(hostname);
  if (product) {
    setTab(document, { title: product.name, icon: product.icon, touchIcon: null });
    return;
  }

  let branding: PublicBranding | null;
  try {
    branding = await fetchBranding();
  } catch {
    return;
  }

  if (!branding) {
    return;
  }

  const api = apiBaseUrl.replace(/\/$/, '');
  setTab(document, {
    title: branding.title,
    icon: branding.iconPath ? `${api}/${branding.iconPath}` : null,
    touchIcon: branding.touchIconPath ? `${api}/${branding.touchIconPath}` : null,
  });
}

/** `GET public/branding` with plain fetch: anonymous, so it needs none of HttpClient's interceptors. */
export async function fetchPublicBranding(apiBaseUrl: string): Promise<PublicBranding | null> {
  const response = await fetch(`${apiBaseUrl.replace(/\/$/, '')}/public/branding`, { credentials: 'omit' });
  return response.status === 200 ? ((await response.json()) as PublicBranding) : null;
}

/** Points the document's title and icon links at `tab`. */
export function setTab(document: Document, tab: TabBrand): void {
  document.title = tab.title;

  if (tab.icon) {
    // Every rel="icon" link, the SVG one included: a browser picks the SVG over the .ico when both
    // are offered, so leaving it would keep the ComputAll mark on the customer's domain.
    document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]').forEach((link) => link.remove());
    const icon = document.createElement('link');
    icon.rel = 'icon';
    icon.href = tab.icon;
    icon.setAttribute('sizes', 'any');
    document.head.appendChild(icon);
  }

  if (tab.touchIcon) {
    document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')?.setAttribute('href', tab.touchIcon);
  }
}

function onDomain<T extends { readonly domain: string }>(list: ReadonlyArray<T>, hostname: string): T | undefined {
  // Lower-cased, and without the trailing dot of a fully-qualified name (`app.batchbilling.net.`).
  const host = hostname.toLowerCase().replace(/\.$/, '');
  return list.find((b) => host === b.domain || host.endsWith(`.${b.domain}`));
}
